import * as React from 'react';
import { Box, Button, Typography, Grid, Paper, Divider } from '@mui/material';
import { Link } from "react-router-dom"
import { MainPadding } from '../../../components/MainPadding';
import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { PageList } from './PageList';
import { JsonSchemasList } from './JsonSchemaList';
import { Breadcumb } from '../../../components/Breadcumb';
import {
    FolderOutlined,
    SettingsOutlined,
    HomeOutlined,
    OpenInNewOutlined,
    CloudUploadOutlined,
    PublicOutlined,
    TuneOutlined,
} from '@mui/icons-material';
import { SmartFileManagerDialog } from '../../../components/Websites/SmartFileManagerDialog';
import { UsersList } from '../../../components/Websites/UsersList/UsersList';
import { AliasesList } from '../../../components/Websites/AliasesList';
import { RoleSection } from '../../../components/RoleSection';
import { serviceLocator } from '../../../services';
import { notify } from '../../../services/ui-dialog';
import { AdminOrAgencyMemberSection } from '../../../components/AdminOrAgencyMemberSection';
import { AdminOrAgencyownerOrAgentOrDeployerSection } from '../../../components/AdminOrAgencyownerOrAgentOrDeployerSection';
import { ReviewsProvidersList } from '../../../components/ReviewsProvidersList';
import { QualityReportsList } from './QualityReportsList/QualityReportsList';
import { InputLabel, MenuItem, Select, FormControl } from '@mui/material';

const genericWebsiteService = serviceLocator.getWebsiteService();
const paperProps = { style: { height: '100%' } };

interface WebsitesProps {
    websiteKey: string;
}

interface WebsiteState {
    fileManagerOpen: boolean;
    website: any;
    advancedExpanded: boolean;
}

export class Website extends React.Component<WebsitesProps, WebsiteState>{

    constructor(props: any) {
        super(props);
        this.state = {
            fileManagerOpen: false,
            website: null,
            advancedExpanded: false
        };
    }

    loadSite = async () => {
        const website = await genericWebsiteService.getWebsite(this.props.websiteKey);
        if (website.status == null) {
            website.status = "active"
        }
        this.setState({ website });
    }

    public componentDidMount = async () => {
        this.loadSite();
    }

    private globalPropsLink = React.forwardRef((props: any, ref: any) => {
        return <Link ref={ref} to={`main-form/`} {...props} />;
    });

    private homePropsLink = React.forwardRef((props: any, ref: any) => {
        return <Link ref={ref} to={`home/`} {...props} />;
    });

    handleFileManagerOpen = (e: any) => {
        this.setState({ fileManagerOpen: true });
    }

    toggleDailyCheck = async () => {
        await genericWebsiteService.toggleQualityChecks(this.props.websiteKey, this.state.website.enableQualityChecks !== true);
        this.loadSite();
    }

    handleOpenProductionClick = async () => {
        const data = await genericWebsiteService.getInfrastructureData(this.props.websiteKey);
        if (data?.domain != null) {
            window.open(`https://${data.domain}`, '_blank');
        }
        else {
            notify({ message: `"${this.props.websiteKey}" is not deployed.`, severity: 'warning' });
        }
    }

    handleStatusChange = async (event: any) => {
        this.setState({ website: { ...this.state.website, status: event.target.value } });
        const data = await genericWebsiteService.putStatus(this.props.websiteKey, event.target.value);

        this.loadSite();
    };

    public render() {

        if (this.state.website == null) return (null);

        // LIST + DIALOGS
        return (
            <MainPadding>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Breadcumb items={[
                            { to: `/websites/custom-website/`, label: `Custom Website` },
                            { label: `Website ${this.props.websiteKey}` }
                        ]} />
                    </Box>
                    <Button onClick={this.handleFileManagerOpen} startIcon={<FolderOutlined />} size="small">
                        Static
                    </Button>
                </Box>
                <Paper sx={{
                    p: 1.5,
                    mt: 2,
                    mb: 3,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Button variant="contained" color="primary" nativeButton={false} component={this.globalPropsLink} startIcon={<SettingsOutlined />}>Site Config</Button>
                        <Button variant="contained" nativeButton={false} component={this.homePropsLink} startIcon={<HomeOutlined />}>Home</Button>
                        <Button
                            nativeButton={false}
                            component={React.forwardRef((props: any, ref: any) => <a {...props} ref={ref} target="_blank" href={`http://custom-website--${this.props.websiteKey}.hugosites.webdrvn.com`} />)}
                            variant="text"
                            startIcon={<OpenInNewOutlined />}
                        >
                            Preview
                        </Button>
                        <AdminOrAgencyownerOrAgentOrDeployerSection website={this.state.website} agency={this.state.website.agency}>
                            <Button
                                nativeButton={false}
                                component={React.forwardRef((props: any, ref: any) => <Link ref={ref} {...props} to={`/websites/custom-website/${this.props.websiteKey}/deploy/`} />)}
                                variant="text"
                                startIcon={<CloudUploadOutlined />}
                            >
                                Deploy
                            </Button>
                            <Button onClick={this.handleOpenProductionClick} variant="text" startIcon={<PublicOutlined />}>
                                Production Website
                            </Button>
                        </AdminOrAgencyownerOrAgentOrDeployerSection>
                    </Box>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="website-status-label">Status</InputLabel>
                        <Select
                            labelId="website-status-label"
                            id="website-status"
                            value={this.state.website.status}
                            label="Status"
                            onChange={this.handleStatusChange}
                        >
                            <MenuItem value={'active'}>Active</MenuItem>
                            <MenuItem value={'archived'}>Archived</MenuItem>
                        </Select>
                    </FormControl>
                </Paper>
                <Grid container spacing={3} sx={{
                    alignItems: "stretch"
                }}>
                    <Grid
                        size={{
                            lg: 6,
                            md: 6,
                            xs: 12
                        }}>
                        <ErrorBoundary>
                            <PageList paperProps={paperProps} websiteKey={this.props.websiteKey} title={"Pages"} collectionKey={"pages"} />
                        </ErrorBoundary>
                    </Grid>
                    <Grid
                        size={{
                            lg: 6,
                            md: 6,
                            xs: 12
                        }}>
                        <ErrorBoundary>
                            <PageList paperProps={paperProps} websiteKey={this.props.websiteKey} title={"Blogs"} collectionKey={"blogs"} />
                        </ErrorBoundary>
                    </Grid>

                    {/* <Grid item md={4} xs={12}>
                        <ErrorBoundary>
                            <JsonSchemasList paperProps={paperProps} websiteKey={this.props.websiteKey} />
                        </ErrorBoundary>
                    </Grid> */}

                    <AdminOrAgencyMemberSection agency={this.state.website.agency}>
                        <Grid style={{ paddingTop: 0, paddingBottom: 0 }} size={12}></Grid>
                        {!this.state.advancedExpanded ? (
                            <Grid style={{ paddingTop: 0, paddingBottom: 0 }} size={12}>
                                <Divider sx={{ my: 3 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<TuneOutlined />}
                                        onClick={() => this.setState({ advancedExpanded: true })}
                                    >
                                        Advanced Settings
                                    </Button>
                                </Divider>
                            </Grid>
                        ) : (
                            <React.Fragment>
                                <Grid
                                    size={{
                                        md: 4,
                                        xs: 12
                                    }}>
                                    <ErrorBoundary>
                                        <UsersList paperProps={paperProps} websiteKey={this.props.websiteKey} />
                                    </ErrorBoundary>
                                </Grid>
                                <Grid
                                    size={{
                                        md: 4,
                                        xs: 12
                                    }}>
                                    <ErrorBoundary>
                                        <AliasesList paperProps={paperProps} websiteKey={this.props.websiteKey} />
                                    </ErrorBoundary>
                                </Grid>
                                <Grid
                                    size={{
                                        lg: 4,
                                        md: 6,
                                        xs: 12
                                    }}>
                                    <ErrorBoundary>
                                        <QualityReportsList
                                            dailyCheckEnabled={this.state.website.enableQualityChecks === true}
                                            onEnableDailyCheckToggle={this.toggleDailyCheck}
                                            agency={this.state.website.agency}
                                            websiteKey={this.props.websiteKey} />
                                    </ErrorBoundary>
                                </Grid>
                                <RoleSection role="admin">
                                    <Grid
                                        style={{visibility: 'hidden'}}
                                        size={{
                                            md: 4,
                                            xs: 12
                                        }}>
                                        <ErrorBoundary>
                                            <ReviewsProvidersList websiteKey={this.props.websiteKey} />
                                        </ErrorBoundary>
                                    </Grid>
                                </RoleSection>
                            </React.Fragment>
                        )}
                    </AdminOrAgencyMemberSection>

                </Grid>
                <SmartFileManagerDialog
                    assets={false}
                    themeKey="custom-website"
                    websiteKey={this.props.websiteKey}
                    open={this.state.fileManagerOpen}
                    onClose={() => this.setState({ fileManagerOpen: false })}
                    onPick={null} />
            </MainPadding>
        );
    }
}